import React, { useEffect, useState } from 'react';
import { View, Image, Linking } from 'react-native';
import styled from '@emotion/native';
import { H2, H3, BodyText } from '@/shared/components/StyledText';
import { green, sand, mint } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { eventConfig, type EventConfig } from './constants';
import {
  EventService,
  EventAttendeeStylist,
} from '@/services/event/Event.service';
import SentryService from '@/services/sentry/Sentry.service';
import Carousel from '@/shared/components/carousel/Carousel';
import useDimensions from '@/shared/hooks/useDimensions';

interface FeaturedStylistsProps {
  config: EventConfig['featuredStylists'];
}

const formatStylistName = (firstName: string, lastName: string): string => {
  return `${firstName.toUpperCase()} ${lastName.charAt(0).toUpperCase()}.`;
};

const FeaturedStylists: React.FC<FeaturedStylistsProps> = ({ config }) => {
  const { width } = useDimensions();
  const { isDesktop } = useBreakpoint();
  const [stylists, setStylists] = useState<EventAttendeeStylist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [maxCardHeight, setMaxCardHeight] = useState(500);

  // Calculate maximum card height based on bio length
  const calculateMaxHeight = (stylists: EventAttendeeStylist[]) => {
    if (stylists.length === 0) return 500;

    const baseHeight = 200; // Name + image + padding
    const maxBioLength = Math.max(
      ...stylists.map((stylist) => stylist.bio?.length || 0),
    );

    // Estimate height based on bio length (roughly 15-20 characters per line, 20px per line)
    const estimatedBioHeight = Math.ceil(maxBioLength / 20) * 20;
    const totalHeight = baseHeight + estimatedBioHeight + 50; // Extra padding

    return Math.max(totalHeight, 500); // Minimum height of 500px
  };

  // Fetch stylists data
  useEffect(() => {
    const fetchStylists = async () => {
      try {
        setIsLoading(true);
        const data = await EventService.getEventAttendeeStylists();
        setStylists(data);

        // Calculate and set maximum height based on bio lengths
        const calculatedHeight = calculateMaxHeight(data);
        setMaxCardHeight(calculatedHeight);
      } catch (error) {
        SentryService.captureError(
          'Failed to fetch event attendee stylists:',
          error,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchStylists();
  }, []);

  const handleNormalShowClick = async () => {
    try {
      await Linking.openURL(eventConfig.normalShow.eventbriteUrl);
    } catch (error) {
      console.error('Error opening Eventbrite URL:', error);
    }
  };

  return (
    <Section>
      <Content isDesktop={isDesktop}>
        <Header>
          <Title>
            {isDesktop
              ? config.title
              : config.mobileTitle.map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < config.mobileTitle.length - 1 && '\n'}
                  </React.Fragment>
                ))}
          </Title>
          <Description style={{ marginBottom: isDesktop ? 0 : 16 }}>
            Meet our expert stylists at{' '}
            <DescriptionBold onPress={handleNormalShowClick}>
              Not-A-Normal Show.
            </DescriptionBold>
            {isDesktop ? ' ' : '\n\n'}
            Discover your next one-of-a-kind piece in a personalized
            session—book your one-on-one through the EMME app.
          </Description>
        </Header>
      </Content>

      <FullWidthCarouselContainer isDesktop={isDesktop}>
        <Carousel
          maxWidth={width}
          loop
          autoplay
          itemWidth={290}
          itemHeight={maxCardHeight}
        >
          {isLoading ? (
            <StylistCard height={maxCardHeight}>
              <StylistName>Loading...</StylistName>
              <StylistImage source={{ uri: '' }} />
              <StylistDescription>
                Fetching featured stylists...
              </StylistDescription>
            </StylistCard>
          ) : stylists.length > 0 ? (
            // Show stylists (duplicated for loop purposes)
            [...stylists, ...stylists].map((stylist, index) => (
              <StylistCard
                key={`${stylist.id}-${index}`}
                height={maxCardHeight}
              >
                <StylistName>
                  {formatStylistName(
                    stylist.user.firstName,
                    stylist.user.lastName,
                  )}
                </StylistName>
                <StylistImage source={{ uri: stylist.user.profilePicUrl }} />
                <StylistDescription>{stylist.bio}</StylistDescription>
              </StylistCard>
            ))
          ) : (
            // Show empty state
            <StylistCard height={maxCardHeight}>
              <StylistName>No Stylists</StylistName>
              <StylistImage source={{ uri: '' }} />
              <StylistDescription>
                No event attendee stylists available at this time.
              </StylistDescription>
            </StylistCard>
          )}
        </Carousel>
      </FullWidthCarouselContainer>
    </Section>
  );
};

export default FeaturedStylists;

// Styled Components
const Section = styled(View)({
  backgroundColor: green,
  paddingVertical: 80,
});

const Content = styled(View)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  maxWidth: 1200,
  alignSelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: isDesktop ? 80 : 16,
  paddingHorizontal: 32,
}));

const Header = styled(View)({
  alignItems: 'center',
  maxWidth: 500,
});

const Title = styled(H2)({
  fontSize: 34,
  color: sand,
  textTransform: 'uppercase',
  marginBottom: 24,
  textAlign: 'center',
});

const Description = styled(BodyText)({
  fontSize: 16,
  fontWeight: 'normal',
  color: sand,
  textAlign: 'center',
});

const DescriptionBold = styled(BodyText)({
  fontSize: 16,
  fontWeight: 'bold',
  color: sand,
  textAlign: 'center',
  textDecorationLine: 'underline',
});

const FullWidthCarouselContainer = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    width: '100%',
    overflow: 'hidden',
    paddingTop: isDesktop ? 80 : 16,
    paddingBottom: 8,
  }),
);

const StylistCard = styled(View)<{ height?: number }>(({ height = 500 }) => ({
  backgroundColor: mint,
  borderRadius: 12,
  padding: 24,
  width: 290,
  height,
  borderWidth: 1,
  borderColor: sand,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
  marginHorizontal: 12,
}));

const StylistName = styled(H3)({
  fontSize: 24,
  fontWeight: '500',
  color: green,
  textTransform: 'uppercase',
  letterSpacing: 0.05,
  textAlign: 'center',
  marginBottom: 16,
});

const StylistImage = styled(Image)({
  width: 198,
  height: 218,
  borderRadius: 4,
  marginBottom: 16,
});

const StylistDescription = styled(BodyText)({
  fontSize: 14,
  fontWeight: 'normal',
  color: green,
  textAlign: 'center',
  marginTop: 16,
});
